import type { FactoriesFactory, OrganizationsIntegration } from "@/api-client";
import { getApiErrorMessage } from "@/lib/errors";

const ORGANIZATION_SUFFIX_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

export function githubIntegrationOwner(integration: OrganizationsIntegration): string | undefined {
  const owner = integration.status?.metadata?.owner;
  return typeof owner === "string" && owner.trim() ? owner.trim() : undefined;
}

export function githubOwnerFromConnections(
  connections: OrganizationsIntegration[],
  integrationId: string,
): string | undefined {
  const integration = connections.find((item) => item.metadata?.id === integrationId);
  return integration ? githubIntegrationOwner(integration) : undefined;
}

export function shouldNameOrganizationFromGitHub(factory: FactoriesFactory | null): boolean {
  return factory?.onboarding?.initial === true;
}

export function randomOrganizationSuffix(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => ORGANIZATION_SUFFIX_ALPHABET[byte % ORGANIZATION_SUFFIX_ALPHABET.length]).join("");
}

export function organizationIdentityFromOwner(owner: string, suffix?: string): { name: string; slug: string } {
  const base = owner.trim();
  const slugBase = slugifyOrganizationOwner(base);
  if (!suffix) {
    return { name: base, slug: slugBase };
  }
  // Only the slug needs to stay unique; the name always mirrors the plain
  // GitHub owner so retries on a slug collision don't rename the org.
  return { name: base, slug: `${slugBase}-${suffix}` };
}

export function isOrganizationIdentityTaken(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("already in use") ||
    normalized.includes("already used") ||
    normalized.includes("invalid organization update") ||
    normalized.includes("duplicate key")
  );
}

/**
 * True when the slug already derives from this GitHub owner, either as the
 * plain owner slug or with the random collision suffix. A repeat visit to
 * onboarding must not rename the organization again.
 */
export function organizationSlugMatchesOwner(currentSlug: string, owner: string): boolean {
  const slugBase = slugifyOrganizationOwner(owner);
  if (currentSlug === slugBase) {
    return true;
  }
  return new RegExp(`^${slugBase}-[a-z0-9]{6}$`).test(currentSlug);
}

export async function nameOrganizationFromGitHubOwner(args: {
  owner: string;
  currentSlug: string;
  update: (identity: { name: string; slug: string }) => Promise<string | undefined>;
  randomSuffix?: () => string;
}): Promise<string | undefined> {
  const nextSuffix = args.randomSuffix ?? randomOrganizationSuffix;
  let identity = organizationIdentityFromOwner(args.owner);
  if (organizationSlugMatchesOwner(args.currentSlug, args.owner)) {
    return args.currentSlug;
  }

  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      return await args.update(identity);
    } catch (error) {
      if (!isOrganizationIdentityTaken(getApiErrorMessage(error, "")) || attempt === 4) {
        throw error;
      }
      identity = organizationIdentityFromOwner(args.owner, nextSuffix());
    }
  }

  return undefined;
}

export async function completeInitialOrganizationIdentity(args: {
  factory: FactoriesFactory | null;
  owner: string | undefined;
  currentSlug: string;
  update: (identity: { name: string; slug: string }) => Promise<string | undefined>;
}): Promise<string> {
  if (!shouldNameOrganizationFromGitHub(args.factory) || !args.owner) {
    return args.currentSlug;
  }

  const nextSlug = await nameOrganizationFromGitHubOwner({
    owner: args.owner,
    currentSlug: args.currentSlug,
    update: args.update,
  });
  return nextSlug || args.currentSlug;
}

function slugifyOrganizationOwner(owner: string): string {
  const slug = owner
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "org";
}
